/**
 * DashboardSelector Component
 * Top bar for selecting and managing analytics dashboards
 */

import React from 'react';
import { Box, Select, MenuItem, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AnalyticsDashboardDto } from '@/api/types/dtos/analytics/AnalyticsDto';

interface DashboardSelectorProps {
  dashboards: AnalyticsDashboardDto[];
  selectedDashboardId: string | null;
  onSelectDashboard: (dashboardId: string) => void;
  onCreateDashboard: () => void;
  isLoading?: boolean;
  canCreate?: boolean;
}

export const DashboardSelector: React.FC<DashboardSelectorProps> = ({
  dashboards,
  selectedDashboardId,
  onSelectDashboard,
  onCreateDashboard,
  isLoading = false,
  canCreate = true,
}) => {
  // Sort dashboards alphabetically for consistent ordering
  const sortedDashboards = React.useMemo(() => {
    return [...dashboards].sort((a, b) => a.name.localeCompare(b.name));
  }, [dashboards]);

  const handleChange = (event: any) => {
    onSelectDashboard(event.target.value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        px: 0,
        mb: 3,
        borderBottom: '1px solid',
        borderColor: 'var(--border-color)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'var(--text-primary)',
          }}
        >
          Dashboard
        </Typography>

        {sortedDashboards.length > 0 && (
          <Select
            value={selectedDashboardId || ''}
            onChange={handleChange}
            disabled={isLoading}
            sx={{
              minWidth: 250,
              height: 40,
              borderColor: 'var(--border-color)',
              borderWidth: 1,
              backgroundColor: 'var(--background-paper)',
            }}
            displayEmpty
          >
            {sortedDashboards.map((dashboard) => (
              <MenuItem key={dashboard.id} value={dashboard.id}>
                {dashboard.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>

      {canCreate && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateDashboard}
          disabled={isLoading}
          sx={{
            fontWeight: 600,
            borderRadius: 'var(--button-border-radius)',
            backgroundColor: 'var(--primary)',
            '&:hover': {
              backgroundColor: '#1a2244',
            },
          }}
        >
          New Dashboard
        </Button>
      )}
    </Box>
  );
};
